import React from "react";
import { motion } from "framer-motion";
import { Compass, Quote, Users, ShieldCheck, Globe } from "lucide-react";
import { Section } from "@/components/Primitives";

const EASE = [0.22, 0.61, 0.36, 1];

const MAPS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/1e641627-b4a5-48d2-9cf6-4476cf306fa6/images/87a6c667d711864096a5213044353728019d1a112c4d9db15fa1688b91fd4f95.jpeg";

const PILLARS = [
  { icon: Users, l1: "Accompagnement", l2: "personnalisé" },
  { icon: ShieldCheck, l1: "Transparence", l2: "et confiance" },
  { icon: Globe, l1: "Expertise", l2: "internationale" },
];

/** Carte Australie → Canada avec trajectoire animée et avion en vol. */
const JourneyMap = () => (
  <div className="relative mx-auto w-full max-w-[380px]" data-testid="founder-journey">
    <img src={MAPS_IMG} alt="Carte de l'Australie et du Canada" loading="lazy" className="w-full h-auto rounded-xl" />

    {/* Trajectoire + avion */}
    <svg viewBox="0 0 100 149" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
      <defs>
        <linearGradient id="wte-route" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#C5A059" stopOpacity="0.35" />
          <stop offset="55%" stopColor="#F2D9A0" stopOpacity="1" />
          <stop offset="100%" stopColor="#C5A059" stopOpacity="0.45" />
        </linearGradient>
      </defs>

      <motion.path
        id="wte-route-path"
        d="M62.6 134 C 51 112, 60.5 86, 73.2 68"
        fill="none"
        stroke="#E8C87E"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeDasharray="0.7 2.4"
        className="animate-dash-fine"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.8, ease: EASE }}
      />

      {/* Avion en vol sur la trajectoire */}
      <g>
        <path
          d="M-3.4 0 L2.6 -1.9 L1.6 0 L2.6 1.9 Z"
          fill="#F5DEA8"
          transform="translate(62.6,134)"
        >
          <animateMotion dur="11s" repeatCount="indefinite" rotate="auto" calcMode="linear">
            <mpath href="#wte-route-path" />
          </animateMotion>
        </path>
      </g>
    </svg>

    {/* Étiquettes destinations */}
    <div className="absolute right-[1%] top-[40%] flex items-center gap-2 rounded-full border border-white/15 bg-navy/70 px-3 py-1.5 backdrop-blur-sm animate-float-slow">
      <span className="text-sm leading-none">🇨🇦</span>
      <span className="text-[10px] uppercase tracking-[0.18em] text-white/85">Canada</span>
    </div>
    <div className="absolute left-[1%] bottom-[8%] flex items-center gap-2 rounded-full border border-white/15 bg-navy/70 px-3 py-1.5 backdrop-blur-sm animate-float-slow" style={{ animationDelay: "-5s" }}>
      <span className="text-sm leading-none">🇦🇺</span>
      <span className="text-[10px] uppercase tracking-[0.18em] text-white/85">Australie</span>
    </div>
  </div>
);

export const FounderVisionSection = () => (
  <section data-testid="founder-vision-section" className="relative overflow-hidden bg-navy">
    <div className="pointer-events-none absolute -bottom-32 -left-24 h-[30rem] w-[30rem] rounded-full bg-gold/10 blur-[120px]" />
    <Section className="relative z-10 py-20 sm:py-24">
      {/* En-tête fondateur */}
      <motion.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex flex-col items-start gap-5 sm:flex-row sm:items-center"
      >
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-gold/60 font-heading text-xl tracking-[0.08em] text-gold" aria-hidden="true">
          AB
        </span>
        <div>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold/80">
            <Compass size={13} /> Une vision née du voyage
          </span>
          <h2 data-testid="founder-name" className="mt-1.5 font-heading text-3xl text-white sm:text-4xl">Aïssa Belazi</h2>
          <p className="mt-1 text-xs uppercase tracking-[0.22em] text-gold">Fondateur de Work Travel Expert</p>
        </div>
      </motion.div>

      {/* Carte parcours */}
      <motion.div
        initial={{ opacity: 0, y: 26 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
        className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-7 sm:p-10"
      >
        <h3 data-testid="founder-vision-title" className="font-heading text-2xl text-white sm:text-3xl">
          <span className="text-gold">Mon parcours</span>, de l'Australie au Canada <span className="ml-1">🇦🇺 🇨🇦</span>
        </h3>
        <div className="gold-line mt-4" />

        <div className="mt-8 grid items-start gap-10 lg:grid-cols-[1fr_0.9fr] lg:gap-14">
          <div className="space-y-5 text-sm leading-relaxed text-white/85 sm:text-base">
            <p>
              Passionné par la mobilité internationale et les opportunités qu'offre le monde, j'ai créé{" "}
              <span className="text-gold">Work Travel Expert</span> pour accompagner les francophones dans leurs projets à l'étranger.
            </p>
            <p>Australie ou Canada, chaque destination ouvre la porte à de nouvelles expériences, à des rencontres et à un avenir différent.</p>
            <p>Mon engagement est simple : vous offrir un accompagnement clair, humain et personnalisé à chaque étape.</p>

            <div className="flex items-start gap-4 pt-2">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/50">
                <Quote size={17} className="text-gold" />
              </span>
              <p className="font-heading text-lg leading-snug text-gold sm:text-xl">
                Derrière chaque projet, il y a une histoire. La vôtre.
              </p>
            </div>
          </div>

          <JourneyMap />
        </div>

        {/* Piliers */}
        <div className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.l1}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * i, ease: EASE }}
              data-testid={`founder-pillar-${i}`}
              className="flex items-center gap-4"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold">
                <p.icon size={19} />
              </span>
              <p className="text-sm leading-snug text-white/85">
                {p.l1}
                <br />
                {p.l2}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </Section>
  </section>
);
